/**
 * CAOS-379 / CAOS-384 — THE list surface. One of them, for everything we ship.
 *
 * A list of things looks and behaves the same wherever a person meets it: an object list in an app,
 * a list inside Setup, anything showing more than one of the same kind of record. The same heading
 * and count, the same New, the same five ways of reading it, the same bar of controls, the same card
 * styling. That is not a house style — it is the whole reason a workspace feels like one product
 * rather than several.
 *
 * So this module owns the surface, and callers describe their records to it rather than drawing
 * their own. What differs between an object list and a Setup list is only WHERE the records come
 * from and what happens when they are narrowed:
 *
 *   • An object list holds ONE PAGE of a set that lives in the store, so every control has to ask
 *     the Engine again. Searching the rows on screen would search the first two hundred records and
 *     call the answer "no matches" — confidently, and wrongly.
 *   • A Setup list holds ALL of its records already (there are twenty permission sets, not twenty
 *     thousand), so the same controls are answered from what is in hand, with no round trip.
 *
 * That difference is the `query` seam and nothing else. Everything a person sees is shared.
 */
import { makeIcon, isIconName } from '@caos/ui';
import type { CaosCardItem, CaosSplitItem, CaosKanbanColumn, CaosKanbanCard, CaosMenuItem } from '@caos/ui';
import { MODE_LABEL, DEFAULT_MODE, rememberedMode, rememberMode, type ListMode } from './list-modes.js';

/** One value a board stands a column up from, or a grouped view gathers under. */
export interface CollectionStage {
  readonly value: string;
  readonly label: string;
  readonly intent?: string;
}

/** One column of the table body. */
export interface CollectionColumn {
  readonly key: string;
  readonly label: string;
  readonly align?: 'start' | 'end';
  /** Whether the heading offers to order by this column. Server-backed lists say so per column. */
  readonly sortable?: boolean;
  /**
   * CAOS-1677 — whether a cell in this column is edited in place.
   *
   * ALREADY DECIDED by the time it arrives here. The caller has weighed the view's declaration
   * against the person's permissions, the field's storage kind and the column's job in the table;
   * this surface draws what it is handed and asks no further question, exactly as it does with
   * `sortable`. A list that offers no `onCellCommit` gets no editors, because an editor whose
   * commit goes nowhere is worse than none.
   */
  readonly editable?: boolean;
}

/**
 * CAOS-1861 — one view a person may choose.
 *
 * `key` and `label` are all this surface needed before, and they are still all it needs to DRAW the
 * chooser; everything else exists so the management menu can offer only what the Engine will
 * actually allow. All of it is optional, so every existing caller's `{key, label}` entries keep
 * compiling and keep meaning what they meant.
 *
 * `canEdit`/`canDelete` ARRIVE ALREADY DECIDED, from the `listViews` routes, per view. The route's
 * own words on a package's shipped view are the reason this is not computed here: "Nobody edits it
 * in place — an admin changes it by deploying, and a person changes it by cloning. Saying so here
 * is what stops a surface offering an affordance that can only fail." Shared TO you is not yours
 * either, and the route reports both false for that case too. A surface that worked either rule out
 * for itself would be a second opinion on a settled question, and the two would drift.
 */
export interface CollectionView {
  readonly key: string;
  readonly label: string;
  /** Where it came from. Absent ⇒ treated as a package view, which is what every caller before this had. */
  readonly source?: 'package' | 'personal';
  /**
   * The row's id, for a personal view only — a package's view has no row and therefore no id.
   *
   * It is carried here rather than looked up by the handler because that absence is what makes "a
   * package's view cannot be deleted" a property of where the two live rather than a check somebody
   * has to remember to write: the delete route goes by id, so a handler with no id in hand has a
   * package view and nothing to send.
   */
  readonly id?: string;
  /** Whether THIS person may rename it. Absent ⇒ no. */
  readonly canEdit?: boolean;
  /** Whether THIS person may delete it. Absent ⇒ no. */
  readonly canDelete?: boolean;
}

/** One record, described once, drawn five ways. */
export interface CollectionRow {
  readonly id: string;
  /** The table body's cells, keyed by column. A cell may be a string or a `{ node }` element. */
  readonly cells: Record<string, unknown>;
  /** The name every other body leads with. */
  readonly title: string;
  /** The quiet line under the name on a card, a board card, and a lane. */
  readonly subtitle?: string;
  /** Where this record opens. */
  readonly href?: string;
  /** Which board column it belongs in (the stored value, `''` for unset). */
  readonly stageValue?: string;
  /** groupKey → the label this record gathers under, for each offered grouping. */
  readonly groups?: Readonly<Record<string, string>>;
  /** The full field set the split view's detail pane reads. */
  readonly detail?: readonly { label: string; value: string; href?: string }[];
  /** The text an in-memory search matches against. Absent ⇒ the title. */
  readonly searchText?: string;
  /**
   * Up to two counted facts, shown along the foot of a card. This is the row the reference card
   * carries — “3 contacts · 0 orders” — and it is what makes a card worth reading rather than a
   * name in a box. Anything beyond the first two is dropped rather than wrapped.
   */
  readonly metrics?: readonly { readonly value: string; readonly label: string }[];
}

/** What a list is, and what it can do. */
export interface CollectionSpec {
  /** Remembers which way this particular list was last read. Must be stable and unique. */
  readonly key: string;
  readonly eyebrow?: string;
  readonly heading: string;
  readonly description?: string;
  readonly columns: readonly CollectionColumn[];
  readonly rows: readonly CollectionRow[];
  /** Whether more records exist beyond the ones supplied. */
  readonly hasMore?: boolean;
  /** What a person is searching over, in their words ("Search accounts by name…"). */
  readonly searchPlaceholder?: string;
  /** CAOS-704 — `false` draws no search box, for a list whose author offers no search. Absent ⇒ one is drawn. */
  readonly searchable?: boolean;
  /** The stages a board stands its columns up from, in order. Absent ⇒ no board is offered. */
  readonly stages?: readonly CollectionStage[];
  /** The ways this list may be gathered. Empty/absent ⇒ no grouped view is offered. */
  readonly groupBy?: readonly { readonly key: string; readonly label: string }[];
  /**
   * CAOS-467 — the presentation this list opens on when nobody has chosen otherwise: the list
   * view's declared `presentation`, read by the caller. Absent, or naming a mode this particular
   * list cannot offer (no stage/nothing to group by), ⇒ the platform default (`card`) stands, the
   * same fallback {@link rememberedMode} has always applied.
   */
  readonly defaultMode?: ListMode;
  /**
   * CAOS-467 — which `groupBy` entry the grouped view opens gathered under: the list view's
   * declared `groupBy` field, read by the caller. Absent, or naming a field not present in
   * `groupBy` above, ⇒ the first offered field stands, same as before this existed.
   */
  readonly groupByDefault?: string;
  /** Ways to order it, above the list so they apply to whatever body is on screen. */
  readonly sortOptions?: readonly { readonly value: string; readonly label: string }[];
  /** Which order is in force. */
  readonly sortValue?: string;
  /** Saved views to choose between, and which is open. */
  readonly views?: readonly CollectionView[];
  readonly viewKey?: string;
  /**
   * CAOS-1861 — the view-management menu: make a view, rename one, copy one, delete one.
   *
   * ALL FOUR OR NONE, by the same pairing rule `onCellCommit` states above: the menu is drawn only
   * when the caller supplies the handlers, because a menu whose items go nowhere is worse than no
   * menu. A caller that cannot write views — a `list`-kind page, which has no `view` address of its
   * own to switch to — supplies none and the surface draws the chooser alone, exactly as before.
   *
   * WHICH ITEMS ARE OFFERED IS NOT THIS SURFACE'S DECISION. Rename and Delete appear only for a
   * view whose own `canEdit`/`canDelete` say so, and those come from the Engine per view. This
   * surface draws what it is handed and asks no further question — the same contract `sortable` and
   * `editable` already follow on a column.
   */
  readonly onViewCreate?: () => void;
  readonly onViewRename?: (view: CollectionView) => void;
  readonly onViewClone?: (view: CollectionView) => void;
  readonly onViewDelete?: (view: CollectionView) => void;
  /** Controls that belong beside the heading (New, and anything else the list offers). */
  readonly actions?: readonly HTMLElement[];
  /** Anything that belongs under the list (a note, a panel). */
  readonly footer?: readonly HTMLElement[];
  /** What to say when there is nothing to show, worded for WHY there is nothing. */
  readonly emptyLabel: (state: CollectionState) => string;
  readonly onOpen?: (id: string) => void;
  readonly onView?: (viewKey: string) => void;
  /**
   * CAOS-1677 — a cell in an `editable` column was committed: write it.
   *
   * Absent ⇒ no column is drawn editable however it was declared, which is the belt that keeps a
   * half-wired list impossible rather than merely discouraged. The two have to arrive together;
   * drawing the control without the save is the one outcome worse than drawing neither, because
   * the person believes the record took the value.
   *
   * `previous` is what the cell held before this edit, for a caller that has to put it back. The
   * surface does not put it back itself: whether a refused value stays on screen depends on WHY it
   * was refused, and only the caller that attempted the write knows that.
   *
   * EVERY COMMIT MUST BE ANSWERED, with exactly one of the two:
   *
   *   `settle(saved)`  — the write landed. Clears that cell's unsaved marker by moving its
   *                      baseline to the value that was written. It is the only thing that moves
   *                      that marker, which means "not saved yet" and nothing else.
   *   `refuse(value)`  — it did not land. Leaves the typed value and the marker, which is then a
   *                      true statement, AND lets the same value be committed again. Without it
   *                      the cell is still holding the refused value, so a second Enter emits
   *                      nothing and the person has to change the text and change it back.
   *
   * Both take the value the answer is ABOUT, so an answer to an older save cannot clear a marker
   * for, or discard, a newer edit still awaiting its own. Both return false when that cell is no
   * longer drawn — a repaint, or a row scrolled out of a virtual window — which a caller can use
   * to decide whether the screen still needs refreshing.
   */
  readonly onCellCommit?: (edit: {
    readonly id: string;
    readonly column: string;
    readonly value: string;
    readonly previous: string;
    readonly settle: (saved: string) => boolean;
    readonly refuse: (refused: string) => boolean;
  }) => void;
  /**
   * Ask the source again. Absent ⇒ this list holds all of its records, and the controls are answered
   * from what is in hand.
   */
  readonly query?: (state: CollectionState) => void;
  /** The narrowing controls, when the caller has them. */
  readonly filters?: HTMLElement;
  /** Load the next page. Absent ⇒ no "show more" is offered even if `hasMore`. */
  readonly onMore?: () => void;
  readonly loading?: boolean;
  /**
   * CAOS-583 — the route this list is open at, so the header can offer an inline affordance to this
   * screen's own help topic when one is anchored to it. Absent ⇒ the affordance falls back to
   * `location.pathname`, which is already correct for every caller running in a real browser; only
   * tests exercising a specific route need to pass this explicitly.
   */
  readonly pathname?: string;
}

/** What the reader has asked of this list. */
export interface CollectionState {
  search: string;
  sortValue: string;
  groupKey: string;
}

/** Which ways THIS list may be read, given what its records support. */
export function modesFor(spec: CollectionSpec): readonly ListMode[] {
  const modes: ListMode[] = ['card', 'table'];
  if (spec.stages && spec.stages.length > 0) modes.push('kanban');
  modes.push('split');
  if (spec.groupBy && spec.groupBy.length > 0) modes.push('group');
  return modes;
}

/** In-memory narrowing, for a list that already holds everything it is showing. */
function narrow(rows: readonly CollectionRow[], search: string): readonly CollectionRow[] {
  const q = search.trim().toLowerCase();
  if (q === '') return rows;
  return rows.filter((r) => (r.searchText ?? r.title).toLowerCase().includes(q));
}

/** In-memory ordering. `sortValue` is `<key>:<asc|desc>` over the TABLE cells' text. */
function order(rows: readonly CollectionRow[], sortValue: string): readonly CollectionRow[] {
  if (sortValue === '') return rows;
  const [key, dir] = sortValue.split(':');
  const sign = dir === 'desc' ? -1 : 1;
  const text = (row: CollectionRow): string => {
    const cell = row.cells[key!];
    if (cell == null) return '';
    if (typeof cell === 'object' && 'node' in (cell as Record<string, unknown>)) {
      return String((cell as { node: HTMLElement }).node.textContent ?? '');
    }
    return String(cell);
  };
  // A copy: ordering the caller's array in place would reorder what it still holds a reference to.
  return [...rows].sort((a, b) => sign * text(a).localeCompare(text(b), undefined, { numeric: true }));
}

function cardsOf(rows: readonly CollectionRow[]): CaosCardItem[] {
  return rows.map((r) => ({
    id: r.id,
    title: r.title,
    ...(r.subtitle ? { subtitle: r.subtitle } : {}),
    ...(r.href ? { href: r.href } : {}),
    ...(r.metrics && r.metrics.length > 0 ? { metrics: r.metrics.slice(0, 2).map((m) => ({ ...m })) } : {}),
  }));
}

function lanesOf(rows: readonly CollectionRow[]): CaosSplitItem[] {
  return rows.map((r) => {
    const detail = (r.detail ?? []).map((f) => ({ label: f.label, value: f.value, ...(f.href ? { href: f.href } : {}) }));
    return { id: r.id, title: r.title, secondary: detail.slice(0, 2), detail };
  });
}

/**
 * The board's columns. Every stage keeps its column, including the ones nothing is sitting in — an
 * empty stage is information, and a column that vanishes when it empties makes the board a different
 * shape every time you look at it. Records with no stage collect in their own leading column rather
 * than being dropped.
 */
function boardOf(rows: readonly CollectionRow[], stages: readonly CollectionStage[]): CaosKanbanColumn[] {
  const buckets = new Map<string, CaosKanbanCard[]>([['', []]]);
  for (const stage of stages) buckets.set(stage.value, []);
  for (const row of rows) {
    const value = row.stageValue ?? '';
    const bucket = buckets.get(value) ?? buckets.get('')!;
    bucket.push({ id: row.id, title: row.title, ...(row.subtitle ? { subtitle: row.subtitle } : {}), ...(row.href ? { href: row.href } : {}) });
  }
  const out: CaosKanbanColumn[] = [];
  const unset = buckets.get('')!;
  if (unset.length > 0) out.push({ value: '', label: 'Unset', cards: unset });
  for (const stage of stages) {
    out.push({ value: stage.value, label: stage.label, ...(stage.intent ? { intent: stage.intent } : {}), cards: buckets.get(stage.value) ?? [] });
  }
  return out;
}

/**
 * Gather under one grouping. Unlike the board, a group with nothing in it is dropped: a heading over
 * blank space is not a group. The leftovers read last, worded as leftovers rather than as a category.
 */
function gather(rows: readonly CollectionRow[], groupKey: string): { label: string; rows: CollectionRow[] }[] {
  // A label is somebody’s own text, so the bucket for “no value” is keyed by something no label can be.
  const NO_VALUE = '\u0000none';
  const buckets = new Map<string, { label: string; rows: CollectionRow[] }>();
  for (const row of rows) {
    const label = row.groups?.[groupKey] ?? '';
    const bucketKey = label === '' ? NO_VALUE : label;
    if (!buckets.has(bucketKey)) buckets.set(bucketKey, { label: label === '' ? 'Not set' : label, rows: [] });
    buckets.get(bucketKey)!.rows.push(row);
  }
  const groups = [...buckets.entries()].map(([k, v]) => ({ ...v, last: k === NO_VALUE }));
  groups.sort((a, b) => (a.last ? 1 : 0) - (b.last ? 1 : 0));
  return groups.map(({ label, rows: r }) => ({ label, rows: r }));
}

function tableOf(
  columns: readonly CollectionColumn[],
  rows: readonly CollectionRow[],
  emptyLabel: string,
  label: string,
  sortable: boolean,
  onSort?: (key: string, dir: 'asc' | 'desc') => void,
  sort?: { key: string; dir: 'asc' | 'desc' },
  onCellCommit?: CollectionSpec['onCellCommit'],
): HTMLElement {
  const table = document.createElement('caos-data-table');
  table.setAttribute('label', label);
  table.setAttribute('empty-label', emptyLabel);
  if (sort) (table as unknown as { sort: unknown }).sort = { key: sort.key, direction: sort.dir };
  (table as unknown as { columns: unknown }).columns = columns.map((c) => ({
    key: c.key,
    label: c.label,
    align: c.align ?? 'start',
    sortable: sortable && c.sortable !== false,
    // CAOS-1677 — an editor is drawn only where the column asked for one AND somebody is listening
    // for the commit. Either half alone draws nothing: a control whose value goes nowhere is the
    // failure this pair exists to make unrepresentable.
    editable: c.editable === true && onCellCommit !== undefined,
  }));
  (table as unknown as { rows: unknown }).rows = rows.map((r) => ({ id: r.id, cells: r.cells }));
  if (onSort) {
    table.addEventListener('caos-sort', (ev) => {
      const detail = (ev as CustomEvent<{ column?: string; direction?: string }>).detail;
      if (!detail?.column) return;
      onSort(detail.column, detail.direction === 'desc' ? 'desc' : 'asc');
    });
  }
  if (onCellCommit) {
    // CAOS-1677 — ONE listener for the whole grid rather than one per cell. The event names the row
    // and the column it came from, so a listener per cell would be fifty closures carrying what the
    // event already carries. `caos-cell-commit` bubbles and is composed, which is what lets it be
    // caught here at all.
    //
    // `caos-cell-edit` is deliberately NOT listened to. It fires per keystroke; writing on it would
    // be a save per character.
    table.addEventListener('caos-cell-commit', (ev) => {
      const d = (ev as CustomEvent<{ id?: unknown; column?: unknown; value?: unknown; previous?: unknown }>).detail;
      if (typeof d?.id !== 'string' || typeof d.column !== 'string') return;
      const id = d.id;
      const column = d.column;
      onCellCommit({
        id,
        column,
        value: typeof d.value === 'string' ? d.value : String(d.value ?? ''),
        previous: typeof d.previous === 'string' ? d.previous : String(d.previous ?? ''),
        // Resolved at CALL time, not here: a save is awaited, and by the time it lands this table
        // may have repainted and dropped the cell. Asking then gets the honest answer instead of a
        // closure over a control that is no longer in the document, and the boolean is handed back
        // so a caller can tell "told the cell" from "there is no cell to tell".
        settle: (saved: string) =>
          (
            table as unknown as { settleCell(rowId: string, columnKey: string, saved: string): boolean }
          ).settleCell(id, column, saved),
        refuse: (refused: string) =>
          (
            table as unknown as { refuseCell(rowId: string, columnKey: string, refused: string): boolean }
          ).refuseCell(id, column, refused),
      });
    });
  }
  return table;
}

/**
 * Draw a list. Returns the surface AND an `update` the caller calls when its records change (a
 * server-backed list re-reads; an in-memory one never needs it).
 */
export function renderCollection(spec: CollectionSpec): { root: HTMLElement; update: (next: CollectionSpec) => void } {
  const root = document.createElement('div');
  root.className = 'sa-surface sa-surface--list';

  let current = spec;
  const modes = modesFor(spec);
  // CAOS-467 — the list view's own declared default, when it named one this list can actually
  // offer; the same platform default (`card`) as always otherwise.
  const defaultMode: ListMode = spec.defaultMode ?? DEFAULT_MODE;
  let mode: ListMode = rememberedMode(spec.key, modes, defaultMode);
  // CAOS-467 — likewise for which grouping the grouped view opens gathered under.
  const defaultGroupKey =
    spec.groupByDefault && spec.groupBy?.some((g) => g.key === spec.groupByDefault)
      ? spec.groupByDefault
      : (spec.groupBy?.[0]?.key ?? '');
  const state: CollectionState = {
    search: '',
    sortValue: spec.sortValue ?? '',
    groupKey: defaultGroupKey,
  };

  const header = document.createElement('caos-page-header');
  const body = document.createElement('div');
  body.className = 'sa-list__body';
  const footer = document.createElement('div');
  footer.className = 'sa-list__footer';

  /** The records to draw: as supplied when a source re-reads, narrowed here when it does not. */
  const visible = (): readonly CollectionRow[] =>
    current.query ? current.rows : order(narrow(current.rows, state.search), state.sortValue);

  const emptyNote = (): HTMLElement => {
    const note = document.createElement('p');
    note.className = 'sa-list__empty';
    note.textContent = current.emptyLabel(state);
    return note;
  };

  const buildBody = (): HTMLElement => {
    const rows = visible();

    if (mode === 'table') {
      return tableOf(
        current.columns,
        rows,
        current.emptyLabel(state),
        current.heading,
        true,
        (key, dir) => {
          state.sortValue = `${key}:${dir}`;
          if (sortChooser) sortChooser.value = state.sortValue;
          if (current.query) current.query(state);
          else paint();
        },
        state.sortValue === '' ? undefined : { key: state.sortValue.split(':')[0]!, dir: state.sortValue.endsWith(':desc') ? 'desc' : 'asc' },
        current.onCellCommit,
      );
    }

    if (rows.length === 0 && mode !== 'kanban') return emptyNote();

    if (mode === 'card') {
      const grid = document.createElement('caos-card-grid');
      (grid as unknown as { items: unknown }).items = cardsOf(rows);
      grid.addEventListener('caos-card-open', (ev) => current.onOpen?.(String((ev as CustomEvent<{ id: string }>).detail.id)));
      return grid;
    }

    if (mode === 'split') {
      const split = document.createElement('caos-split-view');
      (split as unknown as { items: unknown }).items = lanesOf(rows);
      split.addEventListener('caos-split-open', (ev) => current.onOpen?.(String((ev as CustomEvent<{ id: string }>).detail.id)));
      return split;
    }

    if (mode === 'kanban' && current.stages) {
      const board = document.createElement('caos-kanban-board');
      (board as unknown as { columns: unknown }).columns = boardOf(rows, current.stages);
      board.addEventListener('caos-card-open', (ev) => current.onOpen?.(String((ev as CustomEvent<{ id: string }>).detail.id)));
      return board;
    }

    if (mode === 'group' && state.groupKey !== '') {
      const groups = gather(rows, state.groupKey);
      if (groups.length === 0) return emptyNote();
      const holder = document.createElement('div');
      holder.className = 'sa-list__groups';
      for (const group of groups) {
        const section = document.createElement('section');
        section.className = 'sa-list__group';
        const heading = document.createElement('h3');
        heading.className = 'sa-list__group-heading';
        // A group you can see the size of is a group you can decide whether to read.
        heading.textContent = `${group.label} · ${group.rows.length}`;
        // CAOS-1677 — the grouped view is a table per group, so it takes the commit handler too. A
        // column that edits in `table` and not in `group` would be the same quiet half-consumer
        // CAOS-1623 found here: a cell whose editor disappears because somebody gathered the list.
        section.append(
          heading,
          tableOf(
            current.columns,
            group.rows,
            current.emptyLabel(state),
            group.label,
            false,
            undefined,
            undefined,
            current.onCellCommit,
          ),
        );
        holder.append(section);
      }
      return holder;
    }

    // A remembered way of reading that this list no longer offers falls back rather than painting
    // nothing.
    mode = modes.includes(defaultMode) ? defaultMode : DEFAULT_MODE;
    return buildBody();
  };

  const moreButton = (): HTMLElement => {
    const holder = document.createElement('div');
    holder.className = 'sa-list__more';
    const more = document.createElement('caos-button');
    more.setAttribute('variant', 'ghost');
    more.textContent = current.loading ? 'Loading…' : 'Show more';
    more.addEventListener('click', () => current.onMore?.());
    holder.append(more);
    return holder;
  };

  const paint = (): void => {
    header.setAttribute('count', String(visible().length));
    body.replaceChildren(buildBody());
    if (current.hasMore && current.onMore) body.append(moreButton());
  };

  // ── the controls ───────────────────────────────────────────────────────────────────────────────

  const modeGroup = document.createElement('div');
  modeGroup.className = 'sa-list__modes';
  modeGroup.setAttribute('role', 'group');
  modeGroup.setAttribute('aria-label', `How to show ${spec.heading.toLowerCase()}`);
  modeGroup.setAttribute('slot', 'actions');

  const toolbar = document.createElement('div');
  toolbar.className = 'sa-list__toolbar';

  let sortChooser: HTMLSelectElement | null = null;
  const groupPicker = document.createElement('select');

  for (const m of modes) {
    const { icon, label } = MODE_LABEL[m];
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'sa-list__mode';
    button.dataset.mode = m;
    button.setAttribute('aria-pressed', String(m === mode));
    button.setAttribute('aria-label', label);
    button.title = label;
    if (isIconName(icon)) button.append(makeIcon(icon));
    else button.textContent = label;
    button.addEventListener('click', () => {
      if (mode === m) return;
      mode = m;
      rememberMode(spec.key, m);
      for (const other of [...modeGroup.children]) {
        (other as HTMLElement).setAttribute('aria-pressed', String(other === button));
      }
      groupPicker.hidden = mode !== 'group';
      paint();
    });
    modeGroup.append(button);
  }

  /*
   * CAOS-1861 — the two view controls are reachable from `update`, because the set they describe
   * arrives after the first paint. Null when this list has no view controls at all, which is the
   * majority of callers; `applyViews` then does nothing.
   */
  let viewChooser: HTMLSelectElement | null = null;
  let viewMenu: HTMLElement | null = null;
  let rebuildMenuItems: (() => void) | null = null;

  /*
   * CAOS-1861 — the management menu is offered on its own terms, NOT on there being views already.
   *
   * This used to be one `if` over a non-empty `views`, which was right while choosing was the only
   * thing you could do with a view. It is wrong for creating one: an object with nothing shipped is
   * exactly where somebody most wants a view of their own, and gating on a non-empty set would make
   * the first view the one that can never be made.
   *
   * The CHOOSER is still gated on having something to choose — a `select` with no options is a
   * control that looks broken — so the two are now decided separately and a screen can carry the
   * menu alone. It is ABSENT rather than hidden in that case: this codebase's position is that not
   * shown means not in the DOM, held by `test/hidden-guarantee.test.ts`, and `list-modes` asserts
   * the element itself is null for an object with no views. `applyViews` therefore puts it IN and
   * takes it OUT as the set changes, inserting before the menu so a chooser that arrives after the
   * first paint still lands on the correct side of it.
   */
  const offersMenu =
    current.onViewCreate !== undefined ||
    current.onViewRename !== undefined ||
    current.onViewClone !== undefined ||
    current.onViewDelete !== undefined;
  if ((spec.views !== undefined && spec.views.length > 0) || offersMenu) {
    const chooser = document.createElement('select');
    chooser.className = 'sa-list__view';
    chooser.setAttribute('aria-label', `Which ${spec.heading.toLowerCase()} to show`);
    chooser.addEventListener('change', () => current.onView?.(chooser.value));
    viewChooser = chooser;
    // Appended here only when there is already something to choose, so the ordinary case builds the
    // toolbar in one pass and in the authored order; `applyViews` owns it from then on.
    if (spec.views !== undefined && spec.views.length > 0) toolbar.append(chooser);

    /*
     * CAOS-1861 — the management menu, beside the chooser it manages.
     *
     * Offered only when the caller supplied the handlers, on the pairing rule `onCellCommit`
     * states: a control whose action goes nowhere is worse than no control. So a caller with no
     * view-writing of its own gets the chooser alone and is unchanged.
     *
     * `caos-overflow-menu` rather than a hand-built popover. It already carries the whole keyboard
     * model (Enter/Space/ArrowDown to open, Arrow/Home/End to roam, Esc to close and return focus
     * to the trigger), and it PORTALS to the body so nothing around it can clip it. The toolbar is
     * exactly the kind of box that clips, which is the problem that component's own header says it
     * was built for.
     */
    if (offersMenu) {
      const menu = document.createElement('caos-overflow-menu');
      menu.className = 'sa-list__view-menu';
      menu.setAttribute('label', 'Manage views');
      /*
       * WHAT IS OFFERED IS A FUNCTION OF THE SELECTED VIEW, not of the person's appetite.
       *
       * Rename and Delete are present-but-DISABLED rather than absent when the selected view does
       * not permit them. That is deliberate and is the one real judgement in this block: a control
       * that vanishes leaves a person wondering whether the feature exists, while one that is
       * visibly unavailable answers the question. The Engine would refuse the write either way, so
       * nothing here is load-bearing for correctness — this is only about which of two true things
       * to show.
       *
       * Clone is offered for ANY selected view, including a package's, because copying a shipped
       * view into one of your own is precisely how the platform intends you to change it.
       *
       * REBUILT ON EVERY `update`, which is what `applyViews` below exists for: a person's own views
       * arrive after the first paint, and so do the `canEdit`/`canDelete` flags that decide two of
       * these four items. Deciding this once at construction left the menu describing the installed
       * set forever — Rename greyed out on a view that had since been read and is editable.
       */
      rebuildMenuItems = (): void => {
        const selected = (current.views ?? []).find((v) => v.key === current.viewKey);
        const items: CaosMenuItem[] = [];
        if (current.onViewCreate) items.push({ value: 'create', label: 'New view' });
        if (current.onViewClone) {
          items.push({ value: 'clone', label: 'Copy this view', disabled: selected === undefined });
        }
        if (current.onViewRename) {
          items.push({ value: 'rename', label: 'Rename this view', disabled: selected?.canEdit !== true });
        }
        if (current.onViewDelete) {
          items.push({ value: 'delete', label: 'Delete this view', danger: true, disabled: selected?.canDelete !== true });
        }
        (menu as unknown as { items: CaosMenuItem[] }).items = items;
      };
      menu.addEventListener('caos-menu-select', (ev) => {
        const value = (ev as CustomEvent<{ value?: string }>).detail?.value;
        // Re-read the selection at ACTIVATION time rather than trusting the one closed over when
        // the menu was built. The chooser can have moved since, and acting on a stale view would
        // rename or delete something the person is no longer looking at.
        //
        // `viewKey` is the fallback and not the primary: the chooser is what the person last touched,
        // but it is empty while the set is still being read, and the ADDRESS already names a view in
        // that window. Reading only the chooser made every item act on nothing for that moment.
        const live = (current.views ?? []).find((v) => v.key === (chooser.value || current.viewKey));
        if (value === 'create') current.onViewCreate?.();
        if (value === 'clone' && live) current.onViewClone?.(live);
        if (value === 'rename' && live) current.onViewRename?.(live);
        if (value === 'delete' && live) current.onViewDelete?.(live);
      });
      toolbar.append(menu);
      viewMenu = menu;
    }
  }

  const search = document.createElement('input');
  search.type = 'search';
  search.className = 'sa-list__search';
  search.placeholder = spec.searchPlaceholder ?? `Search ${spec.heading.toLowerCase()}…`;
  search.setAttribute('aria-label', search.placeholder);
  let debounce: ReturnType<typeof setTimeout> | null = null;
  search.addEventListener('input', () => {
    state.search = search.value;
    if (current.query) {
      if (debounce) clearTimeout(debounce);
      // A read per keystroke asks the Engine six times for a word nobody has finished typing.
      debounce = setTimeout(() => current.query!(state), 250);
    } else {
      paint();
    }
  });
  if (spec.searchable !== false) toolbar.append(search);

  if (spec.sortOptions && spec.sortOptions.length > 0) {
    sortChooser = document.createElement('select');
    sortChooser.className = 'sa-list__sort';
    sortChooser.setAttribute('aria-label', 'Order');
    const blank = document.createElement('option');
    blank.value = '';
    blank.textContent = 'Order by…';
    sortChooser.append(blank);
    for (const option of spec.sortOptions) {
      const el = document.createElement('option');
      el.value = option.value;
      el.textContent = option.label;
      if (spec.sortValue === option.value) el.selected = true;
      sortChooser.append(el);
    }
    sortChooser.addEventListener('change', () => {
      state.sortValue = sortChooser!.value;
      if (current.query) current.query(state);
      else paint();
    });
    toolbar.append(sortChooser);
  }

  if (spec.filters) {
    const filters = spec.filters;
    filters.hidden = true;
    const narrowButton = document.createElement('button');
    narrowButton.type = 'button';
    narrowButton.className = 'sa-list__tool';
    narrowButton.setAttribute('aria-expanded', 'false');
    narrowButton.setAttribute('aria-label', 'Filters');
    narrowButton.title = 'Filters';
    narrowButton.append(makeIcon('sliders-horizontal'));
    const word = document.createElement('span');
    word.textContent = 'Filter';
    narrowButton.append(word);
    narrowButton.addEventListener('click', () => {
      filters.hidden = !filters.hidden;
      narrowButton.setAttribute('aria-expanded', String(!filters.hidden));
    });
    toolbar.append(narrowButton);
  }

  const refresh = document.createElement('button');
  refresh.type = 'button';
  refresh.className = 'sa-list__tool sa-list__tool--icon';
  refresh.setAttribute('aria-label', 'Refresh');
  refresh.title = 'Refresh';
  refresh.append(makeIcon('refresh-cw'));
  // Records change while somebody is reading the list. Reloading the whole workspace to find out is
  // a heavy way to ask a light question.
  refresh.addEventListener('click', () => {
    if (current.query) current.query(state);
    else paint();
  });
  toolbar.append(refresh);

  groupPicker.className = 'sa-list__group-by';
  groupPicker.setAttribute('aria-label', 'Group by');
  for (const group of spec.groupBy ?? []) {
    const option = document.createElement('option');
    option.value = group.key;
    option.textContent = `Group by ${group.label.toLowerCase()}`;
    groupPicker.append(option);
  }
  // CAOS-467 — without this the control falls back to a native `<select>`'s own default (its
  // FIRST option), which only ever matched `state.groupKey` by coincidence: both were "the first
  // offered field" before a list view could declare a different one to open on.
  groupPicker.value = state.groupKey;
  groupPicker.hidden = mode !== 'group';
  groupPicker.addEventListener('change', () => {
    state.groupKey = groupPicker.value;
    paint();
  });
  toolbar.append(groupPicker);

  if (spec.filters) toolbar.append(spec.filters);

  /** Re-describe the list (new records, a new page, a different saved view). */
  const update = (next: CollectionSpec): void => {
    current = next;
    applyHeader();
    applyViews();
    paint();
  };

  const applyHeader = (): void => {
    if (current.eyebrow) header.setAttribute('eyebrow', current.eyebrow);
    header.setAttribute('heading', current.heading);
    if (current.description) header.setAttribute('description', current.description);
  };

  /**
   * CAOS-1861 — put the current view SET on the two controls that describe it.
   *
   * ## Why this had to exist
   *
   * `update` re-described the header and repainted the rows, and that was the whole of it: the
   * toolbar was built once and never spoken to again. That was correct while the view set was
   * `ctx.meta.listViews`, which is in hand before the first paint and never changes. It stopped
   * being correct the moment a person's own views arrived over the wire — `surface.update(spec())`
   * carried the new set in and nothing on screen moved, so the feature looked implemented and drew
   * nothing. Four of this ticket's tests failed on exactly that and no earlier test could have.
   *
   * THE OPTIONS ARE ONLY REBUILT WHEN THE SET ACTUALLY CHANGED. Every `reread` calls `update`, so an
   * unconditional rebuild would replace the `select`'s children on every search keystroke — and a
   * `select` whose options are replaced loses an open dropdown and the keyboard position inside it.
   * The comparison is over key AND label, because a rename changes only the label.
   */
  const applyViews = (): void => {
    if (viewChooser) {
      const want = current.views ?? [];
      /*
       * DELIMITED, and delimited LEGIBLY. Each field and each entry is separated by a character
       * that cannot occur in a key or a label — a label is free text somebody typed, so no
       * printable character is safe: joining key to label with a space and the entries with
       * nothing would describe `[{a, bc}, {d, e}]` and `[{a, b}, {cd, e}]` identically, two
       * different sets of two views reading as unchanged.
       *
       * The separators were RAW CONTROL CHARACTERS embedded in the template literal, which worked
       * and was unreadable: the independent read on this change reported the comparison as having
       * no delimiter at all, because there is nothing to see between the braces. Named constants
       * instead, so the next reader is not asked to take a byte on trust.
       */
      const UNIT = String.fromCharCode(0x1f);
      const RECORD = String.fromCharCode(0x1e);
      const describe = (key: string, label: string): string => key + UNIT + label;
      const have = [...viewChooser.options].map((o) => describe(o.value, o.textContent ?? '')).join(RECORD);
      const next = want.map((v) => describe(v.key, v.label)).join(RECORD);
      if (have !== next) {
        viewChooser.replaceChildren();
        for (const view of want) {
          const option = document.createElement('option');
          option.value = view.key;
          option.textContent = view.label;
          viewChooser.append(option);
        }
      }
      /*
       * IN or OUT of the DOM, never hidden. A `select` with no options reads as a broken control, and
       * `hidden` is not how this codebase says "not shown" — `test/hidden-guarantee.test.ts` refuses
       * a test that answers "is it hidden?" by reading the property, and `list-modes` asserts the
       * element itself is null for an object with no views.
       *
       * Put BEFORE the menu rather than appended, because a set that arrives after the first paint
       * would otherwise land on the far side of the control that manages it.
       */
      if (want.length > 0 && viewChooser.parentNode === null) {
        if (viewMenu?.parentNode === toolbar) toolbar.insertBefore(viewChooser, viewMenu);
        else toolbar.append(viewChooser);
      } else if (want.length === 0 && viewChooser.parentNode !== null) {
        viewChooser.remove();
      }
      // Set AFTER the options exist. A `select` told to take a value it has no option for keeps its
      // own default (the first), which is how a list opened at `?view=mine` came to show the
      // package's view as selected while drawing the personal one.
      if (current.viewKey !== undefined && want.some((v) => v.key === current.viewKey)) {
        viewChooser.value = current.viewKey;
      }
    }
    rebuildMenuItems?.();
  };

  applyHeader();
  applyViews();
  for (const action of spec.actions ?? []) {
    action.setAttribute('slot', 'actions');
    header.append(action);
  }
  header.append(modeGroup);

  root.append(header, toolbar, body);
  paint();
  if (spec.footer) {
    for (const el of spec.footer) footer.append(el);
    root.append(footer);
  }

  return { root, update };
}
